/**
 * TreatRxPrescriptions.tsx
 *
 * V5 port of the legacy TreatRx/Ayva prescription iframe section
 * (html/html/lib/patient-details/patient-prescriptions.php, pageID 34).
 *
 * Behaviour is a 1:1 port of html/html/js/treatrx/patient.js: the TreatRx
 * middleware calls run directly from the browser (see treatrx.client.ts) and
 * the patient/provider/account context that the legacy PHP rendered
 * server-side comes in as a prop — PrescriptionsTab fetches it once from
 * GET /patients/{id}/prescriptions/context and branches on `provider`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  createPrescriptionGrantFlow,
  type PrescriptionPatient,
  type TreatRxPrescriptionsContext,
} from '@/services/prescriptions.service';
import {
  buildGrantHandoffUrl,
  createEncounter,
  createPatient,
  getEncounterList,
  getPatientEncounter,
  isDoctorGranted,
  isPatientExists,
  updatePatient,
  type AyvaPatientPayload,
} from '@/services/treatrx.client';
import {
  chooseEncounterAction,
  friendlyTreatRxError,
  unwrapEncounterListMarkup,
} from '@/services/treatrx.encounterList';
import { resolveVisibleProviderId } from '@/services/treatrx.providers';

// Legacy global from patient.js: var encDischargeDisp = '10';
const ENC_DISCHARGE_DISP = '10';

type Alert =
  | { kind: 'loading'; text: string }
  | { kind: 'error'; text: string }
  | { kind: 'info'; text: string }
  | null;

// Mirrors the special-character strip applied in loadPrescriptions(...).
function sanitize(value: string): string {
  return (value ?? '').replace(/[`~!@#$%^&*()_|+\-=?;:'"<>{}[\]\\/]/gi, '');
}

function buildAyvaPayload(p: PrescriptionPatient): AyvaPatientPayload {
  return {
    dateOfBirth: p.dateOfBirth,
    firstName: sanitize(p.firstName),
    lastName: sanitize(p.lastName),
    mrn: p.mrn,
    middleName: sanitize(p.middleName),
    gender: p.gender,
    street: sanitize(p.street),
    city: p.city,
    state: p.state,
    zip: p.zip,
    mobileNumber: p.mobileNumber,
    email: p.email,
    weight: p.weight,
    language: p.language,
  };
}

interface TreatRxPrescriptionsProps {
  patientId: number;
  context: TreatRxPrescriptionsContext;
}

export function TreatRxPrescriptions({ patientId, context }: TreatRxPrescriptionsProps) {
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [alert, setAlert] = useState<Alert>(null);
  const [showAllowAccess, setShowAllowAccess] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');
  const [hideNewEncounter, setHideNewEncounter] = useState(false);

  const encounterListRef = useRef<HTMLDivElement>(null);
  const ayvaPatientIdRef = useRef<string | null>(null);
  const ranRef = useRef<string | null>(null);

  // The <select> below falls back to its first option whenever `value` matches
  // none of them, so the provider the flow acts on has to be resolved the same
  // way — otherwise the tab checks a grant for a provider nobody selected.
  const effectiveDoctorId = useMemo(
    () => resolveVisibleProviderId(context?.providers, context?.defaultAyvaDoctorID),
    [context],
  );

  // ── error rendering helpers (mirror the #ayvaAlertWindow writes) ────────────
  const showError = useCallback((text: string) => {
    // Keep the raw body (which can contain SQL) in the console for support and
    // show the user something actionable instead.
    if (text) console.error('TreatRx error:', text);
    setAlert({ kind: 'error', text: friendlyTreatRxError(text) });
  }, []);

  const showThrown = useCallback(
    (e: unknown) => showError(e instanceof Error ? e.message : String(e)),
    [showError],
  );

  // ── iframe loaders ──────────────────────────────────────────────────────────
  const loadEncounterById = useCallback(
    async (ayvaDoctorID: string, encounterId: string) => {
      const enc = await getPatientEncounter(ayvaDoctorID, encounterId);
      setAlert(null);
      setIframeSrc(`${enc.basepath}${encounterId}/discharge/patient?embed=1&jwt=${enc.data}`);
    },
    [],
  );

  const createPatientEncounter = useCallback(
    async (ayvaDoctorID: string, ayvaPatientID: string, encReference: string, accountID: number) => {
      // An empty ayvaPatientID makes the middleware build `"patientId": ,` —
      // malformed JSON that TreatRx always rejects, so stop before the call.
      if (!ayvaPatientID) {
        setAlert({
          kind: 'error',
          text: 'This patient is not linked to TreatRx yet, so an encounter cannot be created.',
        });
        return;
      }
      ayvaPatientIdRef.current = ayvaPatientID;
      setAlert({ kind: 'loading', text: 'Creating patient encounter' });
      const rst = await createEncounter({
        ayvaDoctorID,
        patientId,
        encReference,
        ayvaPatientID,
        encDischargeDisp: ENC_DISCHARGE_DISP,
        accountID,
      });
      setAlert({ kind: 'loading', text: 'Loading patient encounter' });
      const encounterId = String(rst?.data?.uid ?? '');
      const enc = await getPatientEncounter(ayvaDoctorID, encounterId);
      setAlert(null);
      setIframeSrc(
        `${enc.basepath}${encounterId}/discharge/prescription?embed=1&jwt=${enc.data}`,
      );
    },
    [patientId],
  );

  const loadEncounterList = useCallback(
    async (ayvaDoctorID: string, ayvaPatientID: string, encReference: string, accountID: number) => {
      setAlert({ kind: 'loading', text: 'Loading patient encounter list' });
      const html = await getEncounterList(ayvaDoctorID, patientId);
      const container = encounterListRef.current;
      if (!container) return;
      // The response is a JSON array holding the markup, so it has to be
      // unwrapped before the browser can parse it as HTML.
      container.innerHTML = unwrapEncounterListMarkup(html);
      const select = container.querySelector('select');
      if (select) {
        // The server markup carries an inline onchange referencing legacy
        // globals; replace it with a bound listener for parity behaviour.
        select.removeAttribute('onchange');
        select.addEventListener('change', () => {
          loadEncounterById(ayvaDoctorID, select.value).catch(showThrown);
        });
      }

      const action = chooseEncounterAction(container);
      if (action.kind === 'unreadable') {
        // Never treat an unparseable list as "no encounters" — that would
        // create a duplicate encounter behind the user's back.
        container.innerHTML = '';
        setAlert({
          kind: 'error',
          text: 'The encounter list could not be read. Please reload the section.',
        });
        return;
      }
      if (action.kind === 'create') {
        await createPatientEncounter(ayvaDoctorID, ayvaPatientID, encReference, accountID);
      } else {
        await loadEncounterById(ayvaDoctorID, action.encounterId);
      }
    },
    [patientId, loadEncounterById, createPatientEncounter, showThrown],
  );

  // ── main state machine (port of loadPrescriptions) ──────────────────────────
  const loadPrescriptions = useCallback(
    async (ayvaDoctorID: string) => {
      if (!context) return;
      const p = context.patient;
      const payload = buildAyvaPayload(p);

      // reset view state (legacy did a full page reload on entry)
      setIframeSrc('');
      setHideNewEncounter(false);

      // No provider means the account has no aa_ayva_doctors rows. Checking a
      // grant for an empty id always comes back not-granted, and the Allow
      // Access panel would point at an OAuth grant that cannot fix it.
      if (!ayvaDoctorID) {
        setShowAllowAccess(false);
        setLoggedIn(false);
        setAlert({ kind: 'error', text: 'No TreatRx provider is set up for this account.' });
        return;
      }

      setAlert({ kind: 'loading', text: 'Checking grant status' });

      try {
        const granted = await isDoctorGranted(ayvaDoctorID);
        if (granted && granted.isGranted) {
          setAlert({ kind: 'loading', text: 'Checking patient status' });
          setLoggedIn(true);
          setShowAllowAccess(false);

          const rst = await isPatientExists({
            patientID: patientId,
            ayvaDoctorID,
            accountID: p.accountID,
            ftp_patientID: patientId,
            ...payload,
          });

          const fromResults = rst?.data?.results?.[0]?.uid;
          const fromData0 = rst?.data?.[0]?.ayvaPatientID;

          if (fromResults || fromData0) {
            const ayvaPatientID = String(fromResults || fromData0);
            ayvaPatientIdRef.current = ayvaPatientID;
            setAlert({ kind: 'loading', text: 'Syncing patient data' });

            const upd = await updatePatient({
              ayvaPatientID,
              ayvaDoctorID,
              ftp_patientID: patientId,
              accountID: p.accountID,
              ...payload,
            });

            if (upd?.data?.code === 'INVALID_INPUT') {
              setAlert({
                kind: 'error',
                text: `TreatRx responded with the following error: ${upd.data.details}`,
              });
            } else {
              await loadEncounterList(ayvaDoctorID, ayvaPatientID, p.encReference, p.accountID);
            }
          } else {
            setAlert({ kind: 'loading', text: 'Sending patient data to TreatRx' });
            const created = await createPatient({
              ayvaDoctorID,
              ftp_patientID: patientId,
              accountID: p.accountID,
              ...payload,
            });

            const code = created?.data?.code ?? created?.rst?.[0]?.code;
            if (code === 'PATIENT_ALREADY_EXIST') {
              setAlert({ kind: 'loading', text: 'Checking patient status' });
              const re = await isPatientExists({
                patientID: patientId,
                ayvaDoctorID,
                accountID: p.accountID,
                ftp_patientID: patientId,
                ...payload,
              });
              const d0 = re?.data?.[0];
              if (d0 && (d0.ayvaPatientID || d0.uid)) {
                const ayvaPatientID = String(d0.ayvaPatientID || d0.uid);
                ayvaPatientIdRef.current = ayvaPatientID;
                await loadEncounterList(ayvaDoctorID, ayvaPatientID, p.encReference, p.accountID);
              }
            } else if (created?.data?.code === 'INVALID_INPUT') {
              setAlert({
                kind: 'error',
                text: `TreatRx responded with the following error: ${created.data.details}`,
              });
            } else {
              const ayvaPatientID = String(created?.data?.uid ?? '');
              if (!ayvaPatientID) {
                console.error('TreatRx createPatient returned no uid:', created);
                setAlert({
                  kind: 'error',
                  text: 'TreatRx did not return a patient record, so no encounter could be created.',
                });
                return;
              }
              await createPatientEncounter(ayvaDoctorID, ayvaPatientID, p.encReference, p.accountID);
            }
          }
        } else {
          // Not granted: show Allow Access (OAuth grant reuses the legacy tab).
          setAlert(null);
          setShowAllowAccess(true);
          setLoggedIn(false);
        }
      } catch (e) {
        showThrown(e);
      }
    },
    [context, patientId, loadEncounterList, createPatientEncounter, showThrown],
  );

  // ── a different patient has to re-run the whole flow ────────────────────────
  // The tab stays mounted when the chart switches patients, so without this the
  // run guard below still matches the selected provider and the previous
  // patient's encounter list and iframe stay on screen.
  useEffect(() => {
    setInitialized(false);
    setAlert(null);
    setShowAllowAccess(false);
    setLoggedIn(false);
    setIframeSrc('');
    setHideNewEncounter(false);
    ranRef.current = null;
    ayvaPatientIdRef.current = null;
    if (encounterListRef.current) encounterListRef.current.innerHTML = '';
  }, [patientId]);

  // ── initialise selected provider from context, then run the flow ────────────
  useEffect(() => {
    if (context && !initialized) {
      setSelectedDoctorId(effectiveDoctorId);
      setInitialized(true);
    }
  }, [context, initialized, effectiveDoctorId]);

  useEffect(() => {
    if (!context || !initialized) return;
    // Keyed on the patient as well, so the same provider on a different chart
    // is not mistaken for a run that already happened.
    const runKey = `${patientId}:${selectedDoctorId}`;
    if (ranRef.current === runKey) return;
    ranRef.current = runKey;
    void loadPrescriptions(selectedDoctorId);
  }, [context, initialized, patientId, selectedDoctorId, loadPrescriptions]);

  // ── handlers ─────────────────────────────────────────────────────────────────
  function handleProviderChange(next: string) {
    setSelectedDoctorId(next);
  }

  // TreatRx consent happens in the clicker's own Bravado account, which is why
  // legacy has always granted to the signed-in user's own row. Granting on
  // behalf of another provider would bind the wrong tokens to their record.
  const canGrant =
    !!context?.myAyvaDoctorID && selectedDoctorId === context.myAyvaDoctorID;

  async function handleAllowAccess() {
    if (!context) return;
    // Open the tab synchronously, inside the click's user-activation window.
    // A window.open() issued after the await is suppressed by Safari and
    // Firefox popup blockers, which return null rather than throwing -- the
    // button would silently do nothing.
    const tab = window.open('', '_blank');
    try {
      const returnPath = `${window.location.pathname}${window.location.search}`;
      const { handle } = await createPrescriptionGrantFlow(patientId, returnPath);
      const url = buildGrantHandoffUrl(handle);
      if (tab) {
        tab.location.replace(url);
      } else {
        window.open(url, '_blank');
      }
    } catch (e) {
      tab?.close();
      showThrown(e);
    }
  }

  function handleRecheck() {
    // Re-run after the user grants access in the separate tab.
    ranRef.current = null;
    void loadPrescriptions(selectedDoctorId);
  }

  function handleAddEncounter() {
    if (!context) return;
    const ayvaPatientID = ayvaPatientIdRef.current;
    if (!ayvaPatientID) {
      // Nothing to create against — keep the button available.
      setAlert({
        kind: 'error',
        text: 'This patient is not linked to TreatRx yet, so an encounter cannot be created.',
      });
      return;
    }
    const confirmed = window.confirm(
      'Create a new encounter for this patient under the selected provider/prescriber?',
    );
    if (!confirmed) return;
    setHideNewEncounter(true);
    createPatientEncounter(
      selectedDoctorId,
      ayvaPatientID,
      context.patient.encReference,
      context.patient.accountID,
    ).catch(showThrown);
  }

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Prescriptions</h2>
        {/*
          SCRUM-1416. The outer row already wraps, so the group drops below the title —
          but the group itself did not, and a <select> sizes to its longest option up to
          `max-w-[220px]`, which with the "Provider:" label and a `shrink-0` button is
          ~360px against the ~343px a phone leaves. "+ Add Encounter" was the item that
          fell off the edge.
        */}
        <div className="flex items-center gap-3 max-md:w-full max-md:flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-600 max-md:w-full">
            Provider:
            <select
              id="ayvaDoctorSelect"
              value={selectedDoctorId}
              onChange={(e) => handleProviderChange(e.target.value)}
              className="max-w-[220px] rounded-md border border-slate-300 px-2 py-1 text-sm max-md:min-w-0 max-md:flex-1 max-md:max-w-none"
            >
              {context.providers.map((prov) => (
                <option key={prov.ayvaDoctorID} value={prov.ayvaDoctorID}>
                  {prov.name}
                </option>
              ))}
            </select>
          </label>
          {!hideNewEncounter && (
            <Button id="newEncounterButton" size="sm" variant="secondary" onClick={handleAddEncounter}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add encounter
            </Button>
          )}
        </div>
      </div>

      <div ref={encounterListRef} id="encounterList" />

      <div className="container-fluid">
        {showAllowAccess && (
          <div className="space-y-4 py-6 text-center">
            <h5 className="text-sm font-medium text-slate-700">
              {canGrant
                ? 'In order to get your patient information you need to grant access into TreatRx'
                : 'This provider has not granted TreatRx access yet. Only they can authorize it from their own account.'}
            </h5>
            {canGrant && (
              <div className="flex flex-col items-center gap-2">
                <Button onClick={() => void handleAllowAccess()}>Allow Access</Button>
                <button
                  type="button"
                  onClick={handleRecheck}
                  className="text-xs text-slate-500 underline"
                >
                  I&apos;ve granted access &mdash; reload
                </button>
              </div>
            )}
          </div>
        )}

        <div id="ayvaAlertWindow">
          {alert?.kind === 'loading' && (
            <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-700">
              <Loader2 className="h-5 w-5 animate-spin" />
              <strong>{alert.text}</strong>
            </div>
          )}
          {alert?.kind === 'error' && (
            <div className="p-6 text-center text-sm text-rose-600">
              <strong>{alert.text}</strong>
            </div>
          )}
          {alert?.kind === 'info' && (
            <div className="p-6 text-center text-sm text-slate-700">{alert.text}</div>
          )}
        </div>

        {loggedIn && (
          <div id="loggedIn" className="overflow-hidden">
            <iframe
              id="orderHere"
              title="TreatRx"
              src={iframeSrc}
              frameBorder={0}
              style={{
                width: '100%',
                marginTop: '-130px',
                minHeight: '810px',
                overflowY: 'scroll',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
